import { useState, useEffect, useRef } from 'react';
import { api } from '../../context/AuthContext';
import { API, toFullUrl } from '../../config/api';
import { Plus, Trash2, Upload, Image as ImageIcon, Music, Loader2, Calendar, Disc3, X } from 'lucide-react';
import { toast } from 'sonner';

export function MixtapesTab() {
  const [mixtapes, setMixtapes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | 'new' | mixtape object
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetchMixtapes = async () => {
    try {
      const { data } = await api.get(`${API}/mixtapes`);
      setMixtapes(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load mixtapes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMixtapes(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mixtape? This cannot be undone.')) return;
    try {
      await api.delete(`${API}/mixtapes/${id}`);
      setMixtapes((m) => m.filter((x) => x.id !== id));
      toast.success('Mixtape deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleStar = async (id) => {
    try {
      const { data } = await api.put(`${API}/mixtapes/home-featured/${id}`);
      setMixtapes((arr) => arr.map((m) => m.id === id
        ? { ...m, home_featured: data.featured, home_featured_order: data.featured ? data.count - 1 : undefined }
        : m));
      toast.success(data.featured ? `★ Featured on homepage (${data.count}/6)` : '☆ Removed from homepage');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="mixtapes-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
          <Disc3 className="w-6 h-6 text-[#22C55E]" /> MIXTAPES
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setBulkOpen(true)}
            className="px-3 py-2 border border-[#262626] hover:border-[#22C55E] hover:text-[#22C55E] text-xs font-bold uppercase transition-colors flex items-center gap-1.5"
            data-testid="bulk-mixtape-btn"
          >
            <Upload className="w-3.5 h-3.5" /> BULK UPLOAD
          </button>
          <button
            onClick={() => setDialog('new')}
            className="px-4 py-2 bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase text-sm flex items-center gap-2 transition-colors"
            data-testid="new-mixtape-btn"
          >
            <Plus className="w-4 h-4" /> NEW MIXTAPE
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#737373] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : mixtapes.length === 0 ? (
        <div className="py-16 text-center bg-[#141414] border border-[#262626] rounded-xl">
          <Disc3 className="w-12 h-12 text-[#262626] mx-auto mb-3" />
          <p className="text-sm text-[#737373]">No mixtapes yet. Click <span className="text-[#22C55E] font-bold">NEW MIXTAPE</span> to drop your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {mixtapes.map((m) => (
            <div key={m.id} className="bg-[#141414] border border-[#262626] hover:border-[#22C55E]/40 transition-colors group" data-testid={`mixtape-card-${m.id}`}>
              <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                {m.cover_image ? (
                  <img src={toFullUrl(m.cover_image)} alt={m.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Disc3 className="w-14 h-14 text-[#262626]" /></div>
                )}
                <button
                  onClick={() => handleStar(m.id)}
                  className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-full backdrop-blur transition-colors text-lg leading-none ${m.home_featured ? 'bg-[#DAA520] text-black' : 'bg-black/60 text-white/70 hover:bg-[#DAA520]/30 hover:text-[#DAA520]'}`}
                  title={m.home_featured ? `Featured on homepage (#${(m.home_featured_order ?? 0) + 1}/6)` : 'Show on homepage (max 6)'}
                  data-testid={`mixtape-feature-${m.id}`}
                >
                  {m.home_featured ? '★' : '☆'}
                </button>
                {m.price > 0 && (
                  <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-[#22C55E] text-black text-xs font-bold font-mono">${m.price.toFixed(2)}</span>
                )}
                {!m.audio_file && (
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-[#FF3B30]/80 text-white text-[9px] font-bold uppercase">No audio</span>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <p className="font-bold text-sm truncate">{m.title}</p>
                  <p className="text-xs text-[#A3A3A3] truncate">{m.artist}</p>
                  {m.release_date && (
                    <p className="text-[10px] text-[#525252] mt-0.5">{m.release_date}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setDialog(m)}
                    className="flex-1 px-2 py-1.5 text-xs font-bold uppercase border border-[#262626] hover:border-[#22C55E] hover:text-[#22C55E] transition-colors"
                    data-testid={`mixtape-edit-${m.id}`}
                  >EDIT</button>
                  <button
                    onClick={() => handleDelete(m.id)}
                    className="px-2 py-1.5 border border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                    data-testid={`mixtape-delete-${m.id}`}
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <MixtapeDialog
          mixtape={dialog === 'new' ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={() => { fetchMixtapes(); setDialog(null); }}
        />
      )}
      {bulkOpen && (
        <BulkMixtapeDialog
          onClose={() => setBulkOpen(false)}
          onSaved={() => { fetchMixtapes(); setBulkOpen(false); }}
        />
      )}
    </div>
  );
}


function MixtapeDialog({ mixtape, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: mixtape?.title || '',
    artist: mixtape?.artist || '',
    cover_image: mixtape?.cover_image || '',
    audio_file: mixtape?.audio_file || '',
    price: mixtape?.price ?? 0,
    release_date: mixtape?.release_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const coverRef = useRef(null);
  const audioRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file, endpoint) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post(`${API}/upload/${endpoint}`, fd);
    return data.url || data.path;
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Cover must be under 20MB');
      return;
    }
    setUploadingCover(true);
    try {
      const path = await uploadFile(file, 'cover');
      update('cover_image', path);
      toast.success('Cover uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAudioUpload = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error('Audio must be under 200MB');
      return;
    }
    setUploadingAudio(true);
    try {
      const path = await uploadFile(file, 'audio');
      update('audio_file', path);
      toast.success('Audio uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.artist.trim()) {
      toast.error('Title and artist are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (mixtape) {
        await api.put(`${API}/mixtapes/${mixtape.id}`, payload);
        toast.success('Mixtape updated');
      } else {
        await api.post(`${API}/mixtapes`, payload);
        toast.success('Mixtape created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#141414] border-2 border-[#22C55E]/40 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="mixtape-dialog"
      >
        <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4 flex items-center justify-between">
          <h3 className="font-cabinet font-black uppercase text-lg flex items-center gap-2">
            <Disc3 className="w-5 h-5 text-[#22C55E]" /> {mixtape ? 'EDIT MIXTAPE' : 'NEW MIXTAPE'}
          </h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="mixtape-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Cover upload */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">Cover</label>
            <div className="flex items-center gap-3">
              <div className="w-24 h-24 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex items-center justify-center shrink-0">
                {form.cover_image ? (
                  <img src={toFullUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-[#262626]" />
                )}
              </div>
              <input
                ref={coverRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleCoverUpload(e.target.files[0])}
              />
              <button
                type="button"
                onClick={() => coverRef.current?.click()}
                disabled={uploadingCover}
                className="flex-1 h-10 border border-[#262626] hover:border-[#22C55E] hover:text-[#22C55E] text-xs font-bold uppercase flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                data-testid="mixtape-upload-cover"
              >
                {uploadingCover
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                  : <><Upload className="w-3.5 h-3.5" /> {form.cover_image ? 'Change Cover' : 'Upload Cover'}</>
                }
              </button>
            </div>
          </div>

          {/* Audio upload */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">Audio (MP3 / WAV / FLAC / M4A)</label>
            <div className="flex items-center gap-3">
              <div className="w-24 h-24 bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0">
                <Music className={`w-8 h-8 ${form.audio_file ? 'text-[#22C55E]' : 'text-[#262626]'}`} />
              </div>
              <input
                ref={audioRef} type="file" accept="audio/*,.mp3,.wav,.flac,.m4a" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleAudioUpload(e.target.files[0])}
              />
              <button
                type="button"
                onClick={() => audioRef.current?.click()}
                disabled={uploadingAudio}
                className="flex-1 h-10 border border-[#262626] hover:border-[#22C55E] hover:text-[#22C55E] text-xs font-bold uppercase flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                data-testid="mixtape-upload-audio"
              >
                {uploadingAudio
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                  : <><Upload className="w-3.5 h-3.5" /> {form.audio_file ? 'Replace Audio' : 'Upload Audio'}</>
                }
              </button>
            </div>
            {form.audio_file && <p className="text-[10px] text-[#22C55E] font-mono truncate">✓ {form.audio_file.split('/').pop()}</p>}
          </div>

          {/* Title */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">Title *</label>
            <input
              value={form.title} onChange={(e) => update('title', e.target.value)}
              required maxLength={120}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#22C55E] outline-none px-3 py-2 text-sm"
              data-testid="mixtape-title"
            />
          </div>

          {/* Artist */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">Artist *</label>
            <input
              value={form.artist} onChange={(e) => update('artist', e.target.value)}
              required maxLength={120}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#22C55E] outline-none px-3 py-2 text-sm"
              data-testid="mixtape-artist"
            />
          </div>

          {/* Price + Release Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">Price (USD) *</label>
              <input
                type="number" step="0.01" min="0"
                value={form.price} onChange={(e) => update('price', e.target.value)}
                required
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#22C55E] outline-none px-3 py-2 text-sm font-mono"
                data-testid="mixtape-price"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Release Date
              </label>
              <input
                type="date"
                value={form.release_date || ''} onChange={(e) => update('release_date', e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#22C55E] outline-none px-3 py-2 text-sm"
                data-testid="mixtape-release"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full h-12 bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            data-testid="mixtape-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : (mixtape ? 'UPDATE MIXTAPE' : 'CREATE MIXTAPE')}
          </button>
          {form.price > 0 && form.audio_file && (
            <p className="text-[10px] text-center text-[#22C55E]">
              ✓ Ready to sell — will appear in your Store under MIXTAPES
            </p>
          )}
        </form>
      </div>
    </div>
  );
}


function BulkMixtapeDialog({ onClose, onSaved }) {
  const [rows, setRows] = useState([
    { id: 1, title: '', artist: '', price: 0, release_date: '', cover_image: '', audio_file: '', uploading: '' },
  ]);
  const [saving, setSaving] = useState(false);

  const addRow = () => setRows((r) => [...r, { id: Date.now(), title: '', artist: '', price: 0, release_date: '', cover_image: '', audio_file: '', uploading: '' }]);
  const removeRow = (id) => setRows((r) => r.length > 1 ? r.filter(x => x.id !== id) : r);
  const updateRow = (id, k, v) => setRows((r) => r.map(x => x.id === id ? { ...x, [k]: v } : x));

  const uploadFile = async (file, endpoint) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post(`${API}/upload/${endpoint}`, fd);
    return data.url || data.path;
  };

  const handleFileRow = async (id, kind, file) => {
    if (!file) return;
    updateRow(id, 'uploading', kind);
    try {
      const path = await uploadFile(file, kind === 'cover' ? 'cover' : 'audio');
      updateRow(id, kind === 'cover' ? 'cover_image' : 'audio_file', path);
      if (kind === 'audio') {
        const row = rows.find(r => r.id === id);
        if (row && !row.title) {
          const base = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
          updateRow(id, 'title', base);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      updateRow(id, 'uploading', '');
    }
  };

  const handleMultiAudio = (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const stamp = Date.now();
    const baseArtist = rows[0]?.artist || '';
    const basePrice = rows[0]?.price || 0;
    const baseDate = rows[0]?.release_date || '';
    const newRows = arr.map((f, i) => ({
      id: stamp + i,
      title: f.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim(),
      artist: baseArtist, price: basePrice, release_date: baseDate,
      cover_image: '', audio_file: '', uploading: 'audio', _pendingFile: f,
    }));
    setRows((r) => {
      const filtered = r.length === 1 && !r[0].audio_file && !r[0].title ? [] : r;
      return [...filtered, ...newRows];
    });
    (async () => {
      for (const nr of newRows) {
        try {
          const path = await uploadFile(nr._pendingFile, 'audio');
          setRows((r) => r.map(x => x.id === nr.id ? { ...x, audio_file: path, uploading: '' } : x));
        } catch {
          setRows((r) => r.map(x => x.id === nr.id ? { ...x, uploading: '' } : x));
        }
      }
    })();
  };

  const handleSave = async () => {
    const valid = rows.filter(r => r.title && r.artist && r.audio_file && parseFloat(r.price) > 0);
    if (valid.length === 0) {
      toast.error('Need at least one row with title, artist, audio, and price');
      return;
    }
    setSaving(true);
    let saved = 0;
    for (const r of valid) {
      try {
        await api.post(`${API}/mixtapes`, {
          title: r.title, artist: r.artist,
          cover_image: r.cover_image, audio_file: r.audio_file,
          price: parseFloat(r.price) || 0,
          release_date: r.release_date || null,
        });
        saved += 1;
      } catch (err) {
        toast.error(`Failed "${r.title}": ${err?.response?.data?.detail || 'error'}`);
      }
    }
    setSaving(false);
    if (saved > 0) {
      toast.success(`Created ${saved} mixtape${saved === 1 ? '' : 's'}`);
      onSaved();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#141414] border-2 border-[#22C55E]/40 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-mixtape-dialog"
      >
        <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4 flex items-center justify-between z-10">
          <h3 className="font-cabinet font-black uppercase text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#22C55E]" /> BULK UPLOAD MIXTAPES
          </h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-[10px] text-[#A3A3A3] uppercase tracking-wider">
            <label className="cursor-pointer underline hover:text-[#22C55E]">
              <input type="file" multiple accept="audio/*,.mp3,.wav,.flac,.m4a" className="hidden"
                onChange={(e) => handleMultiAudio(e.target.files)} data-testid="bulk-multi-audio" />
              Drop / pick multiple audio files
            </label>
            <span>— each becomes a row. Add cover + price per row.</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[#737373] border-b border-[#262626]">
                  <th className="py-2 px-1 w-12">Cover</th>
                  <th className="py-2 px-1">Title *</th>
                  <th className="py-2 px-1">Artist *</th>
                  <th className="py-2 px-1 w-20">Price *</th>
                  <th className="py-2 px-1 w-32">Release</th>
                  <th className="py-2 px-1 w-32">Audio *</th>
                  <th className="py-2 px-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#1a1a1a]">
                    <td className="py-1 px-1">
                      <label className="block w-10 h-10 bg-[#0A0A0A] border border-[#262626] cursor-pointer hover:border-[#22C55E] overflow-hidden">
                        {r.cover_image ? <img src={toFullUrl(r.cover_image)} alt="" className="w-full h-full object-cover" /> :
                          r.uploading === 'cover' ? <Loader2 className="w-3 h-3 animate-spin text-[#22C55E] m-auto mt-3" /> :
                          <ImageIcon className="w-4 h-4 text-[#404040] m-auto mt-3" />
                        }
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => handleFileRow(r.id, 'cover', e.target.files?.[0])} />
                      </label>
                    </td>
                    <td className="py-1 px-1">
                      <input value={r.title} onChange={(e) => updateRow(r.id, 'title', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs outline-none focus:border-[#22C55E]" />
                    </td>
                    <td className="py-1 px-1">
                      <input value={r.artist} onChange={(e) => updateRow(r.id, 'artist', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs outline-none focus:border-[#22C55E]" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="number" step="0.01" min="0" value={r.price} onChange={(e) => updateRow(r.id, 'price', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs font-mono outline-none focus:border-[#22C55E]" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="date" value={r.release_date} onChange={(e) => updateRow(r.id, 'release_date', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs outline-none focus:border-[#22C55E]" />
                    </td>
                    <td className="py-1 px-1">
                      <label className="block px-2 py-1 border border-[#262626] cursor-pointer hover:border-[#22C55E] text-[10px] text-center uppercase font-bold">
                        {r.audio_file
                          ? <span className="text-[#22C55E]">✓ AUDIO</span>
                          : r.uploading === 'audio'
                            ? <Loader2 className="w-3 h-3 animate-spin inline" />
                            : 'UPLOAD'
                        }
                        <input type="file" accept="audio/*,.mp3,.wav,.flac,.m4a" className="hidden"
                          onChange={(e) => handleFileRow(r.id, 'audio', e.target.files?.[0])} />
                      </label>
                    </td>
                    <td className="py-1 px-1">
                      <button onClick={() => removeRow(r.id)} className="text-[#FF3B30] hover:text-white p-1" title="Remove row">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button onClick={addRow} className="text-[#22C55E] text-xs hover:text-white flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add row
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
              data-testid="bulk-mixtape-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : `CREATE ${rows.filter(r => r.title && r.audio_file).length} MIXTAPES`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

